import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type QueryParams } from '@/lib/api';

/**
 * Hooks für das Drift-Dashboard (Sprint 4, WT-403/WT-405/WT-406).
 *
 * Backend-Endpunkte (Source of Truth):
 *   GET  /drift-report                        -> Aggregat aus v_latest_versions
 *                                                vs. version_targets
 *   POST /health-checks/run                   -> startet Ad-hoc Re-Scan-Job
 *   GET  /health-checks/jobs/{id}             -> Status-Polling
 *
 * Wichtig: Die Wire-Formate hier sind exakt an den Backend-Schemas
 * (app/api/schemas/health_check.py) ausgerichtet. Die Frontend-DTOs
 * (DriftRow, DriftReportResponse, HealthCheckJob) werden daraus gemappt.
 */

// --- Severity / Drift ------------------------------------------------------

export type DriftSeverity = 'info' | 'warning' | 'critical';

export const DRIFT_SEVERITIES: DriftSeverity[] = ['info', 'warning', 'critical'];

export type DriftKind = 'none' | 'patch' | 'minor' | 'major' | 'unknown';

export const DRIFT_KINDS: DriftKind[] = ['none', 'patch', 'minor', 'major', 'unknown'];

// --- Wire-Typen (1:1 zum Backend, NICHT im UI verwenden) -------------------

/** Entspricht schemas.health_check.DriftRow. */
interface WireDriftRow {
  customer_id: string;
  customer_name: string;
  customer_slug: string;
  system_id: string;
  system_name: string;
  system_hostname: string | null;
  service_type_id: string;
  service_type_code: string;
  service_type_display: string | null;
  observed_version: string | null;
  target_version: string | null;
  minimum_version: string | null;
  observed_at: string | null;
  drift: DriftKind;
  severity: DriftSeverity;
  is_eol: boolean;
  eol_at: string | null;
  eol_in_days: number | null;
  age_days: number | null;
  is_stale: boolean;
  notes: string | null;
}

/** Entspricht schemas.health_check.DriftReportSummary. */
interface WireDriftSummary {
  rows_total: number;
  rows_drifted: number;
  rows_critical: number;
  rows_warning: number;
  rows_info: number;
  rows_eol: number;
  rows_stale: number;
}

interface WireDriftReportResponse {
  data: WireDriftRow[];
  summary: WireDriftSummary;
  generated_at: string;
}

/** Entspricht schemas.health_check.HealthCheckJobStatus. */
interface WireHealthCheckJob {
  job_id: string;
  status: HealthCheckJobStatus;
  started_at: string | null;
  finished_at: string | null;
  customer_id: string | null;
  systems_total: number;
  systems_ok: number;
  systems_with_errors: number;
  services_checked: number;
  services_ok: number;
  services_failed: number;
  error: string | null;
}

/** Entspricht schemas.health_check.HealthCheckRunResponse. */
interface WireRunHealthChecksResponse {
  job_id: string;
  status: string;
  customer_id: string | null;
  system_count: number;
}

// --- UI-DTOs ---------------------------------------------------------------

export interface DriftRow {
  /** Stable Composite-Key für React-Lists (system + service_type). */
  id: string;
  customer_id: string;
  customer_name: string;
  system_id: string;
  system_hostname: string;
  service_type: string;
  service_type_label?: string | null;
  /** Aus Observation (observed_version). */
  current_version: string | null;
  /** Aus VersionTarget. */
  target_version: string | null;
  /** Drift-Kategorie vom Backend. */
  drift: DriftKind;
  /** Diff-Label aus der Drift-Kategorie, z.B. "Minor". */
  drift_diff: string | null;
  severity: DriftSeverity;
  /** Letzte Observation (observed_at). */
  last_seen_at: string | null;
  is_eol: boolean;
  eol_at?: string | null;
  is_stale: boolean;
  age_days?: number | null;
  notes?: string | null;
  /** Vom Backend (noch) nicht geliefert - Drawer zeigt Platzhalter. */
  detection_method?: string | null;
  raw_stdout?: string | null;
  history?: DriftHistoryEntry[];
}

export interface DriftHistoryEntry {
  observed_at: string;
  version: string | null;
  detection_method?: string | null;
}

export interface DriftReportStats {
  total_observed: number;
  drifted: number;
  eol: number;
  stale: number;
}

export interface DriftReportResponse {
  data: DriftRow[];
  stats: DriftReportStats;
  /** ISO timestamp des Reports (zum Cache-Anzeigen). */
  generated_at: string;
}

export interface DriftFilter {
  customerId?: string;
  serviceType?: string;
  severity?: DriftSeverity | undefined;
  /** Drift-Kategorie (patch/minor/major/...), clientseitig. */
  driftKind?: DriftKind;
  driftOnly?: boolean;
  eolOnly?: boolean;
  /** Freitext über Hostname / Kunde / Service-Typ / Versionen, clientseitig. */
  searchText?: string;
  /** Nur Zeilen, deren letzte Beobachtung >= N Tage her ist (nie gesehen zählt mit). */
  minAgeDays?: number;
}

export type DriftSortKey = 'severity' | 'customer' | 'hostname' | 'service' | 'age';
export type DriftSortDir = 'asc' | 'desc';

export interface DriftSort {
  key: DriftSortKey;
  dir: DriftSortDir;
}

export interface RunHealthChecksInput {
  systemIds?: string[];
  customerId?: string;
}

export type HealthCheckJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface HealthCheckJob {
  id: string;
  status: HealthCheckJobStatus;
  systemsTotal: number;
  systemsOk: number;
  systemsWithErrors: number;
  servicesChecked: number;
  servicesOk: number;
  servicesFailed: number;
  /**
   * Das Backend füllt die Zähler erst nach Abschluss (report=None während
   * des Laufs -> alle Counts 0). Solange false: Banner "laeuft…" anzeigen.
   */
  hasCounts: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  error?: string | null;
}

// --- Mapping Wire -> DTO ---------------------------------------------------

function driftDiffLabel(drift: DriftKind): string | null {
  switch (drift) {
    case 'patch':
      return 'Patch';
    case 'minor':
      return 'Minor';
    case 'major':
      return 'Major';
    default:
      // "none" (in sync) und "unknown" (kein Ziel/nicht vergleichbar).
      return null;
  }
}

function mapDriftRow(w: WireDriftRow): DriftRow {
  return {
    id: `${w.system_id}::${w.service_type_id}`,
    customer_id: w.customer_id,
    customer_name: w.customer_name,
    system_id: w.system_id,
    // hostname kann null sein -> auf system_name zurückfallen.
    system_hostname: w.system_hostname ?? w.system_name,
    service_type: w.service_type_code,
    service_type_label: w.service_type_display,
    current_version: w.observed_version,
    target_version: w.target_version,
    drift: w.drift,
    drift_diff: driftDiffLabel(w.drift),
    severity: w.severity,
    last_seen_at: w.observed_at,
    is_eol: w.is_eol,
    eol_at: w.eol_at,
    is_stale: w.is_stale,
    age_days: w.age_days,
    notes: w.notes,
  };
}

function mapDriftReport(w: WireDriftReportResponse): DriftReportResponse {
  return {
    data: w.data.map(mapDriftRow),
    stats: {
      total_observed: w.summary.rows_total,
      drifted: w.summary.rows_drifted,
      eol: w.summary.rows_eol,
      stale: w.summary.rows_stale,
    },
    generated_at: w.generated_at,
  };
}

function mapJob(w: WireHealthCheckJob): HealthCheckJob {
  return {
    id: w.job_id,
    status: w.status,
    systemsTotal: w.systems_total,
    systemsOk: w.systems_ok,
    systemsWithErrors: w.systems_with_errors,
    servicesChecked: w.services_checked,
    servicesOk: w.services_ok,
    servicesFailed: w.services_failed,
    // Zähler sind erst nach Abschluss belastbar (report != None).
    hasCounts: w.status === 'completed' || w.systems_total > 0,
    started_at: w.started_at,
    finished_at: w.finished_at,
    error: w.error,
  };
}

const DAY_MS = 86_400_000;

/**
 * Effektives Alter der letzten Beobachtung in Tagen. Priorität: age_days vom
 * Backend, sonst aus last_seen_at berechnet. Nie gesehen (oder unparsebar)
 * gilt als unendlich alt - so matcht die Zeile jeden "aelter als X"-Filter.
 */
export function driftRowAgeDays(row: DriftRow, now: number = Date.now()): number {
  if (row.age_days !== null && row.age_days !== undefined) return row.age_days;
  if (!row.last_seen_at) return Number.POSITIVE_INFINITY;
  const seen = Date.parse(row.last_seen_at);
  if (Number.isNaN(seen)) return Number.POSITIVE_INFINITY;
  return Math.floor((now - seen) / DAY_MS);
}

/**
 * Clientseitige Filter für Kriterien, die das Backend NICHT als Query-Param
 * kennt (severity, eol_only, drift-Kategorie, Freitext, Beobachtungs-Alter).
 * Werden auf die bereits vom Backend gefilterten Zeilen angewendet.
 */
export function applyDriftClientFilters(
  rows: DriftRow[],
  filter: DriftFilter,
  now: number = Date.now()
): DriftRow[] {
  const needle = filter.searchText?.trim().toLowerCase() ?? '';
  return rows.filter((r) => {
    if (filter.severity && r.severity !== filter.severity) return false;
    if (filter.eolOnly && !r.is_eol) return false;
    if (filter.driftKind && r.drift !== filter.driftKind) return false;
    if (filter.minAgeDays !== undefined && driftRowAgeDays(r, now) < filter.minAgeDays) {
      return false;
    }
    if (needle) {
      const haystack = [
        r.system_hostname,
        r.customer_name,
        r.service_type,
        r.service_type_label ?? '',
        r.current_version ?? '',
        r.target_version ?? '',
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

const SEVERITY_RANK: Record<DriftSeverity, number> = {
  info: 0,
  warning: 1,
  critical: 2,
};

/**
 * Stabile clientseitige Sortierung. Ohne sort bleibt die Backend-Reihenfolge
 * erhalten. dir wird über Vorzeichen-Umkehr angewendet (nicht reverse), damit
 * gleichrangige Zeilen ihre relative Reihenfolge behalten.
 */
export function sortDriftRows(
  rows: DriftRow[],
  sort?: DriftSort,
  now: number = Date.now()
): DriftRow[] {
  if (!sort) return rows;
  const cmp = (a: DriftRow, b: DriftRow): number => {
    switch (sort.key) {
      case 'severity':
        return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
      case 'customer':
        return a.customer_name.localeCompare(b.customer_name, 'de');
      case 'hostname':
        return a.system_hostname.localeCompare(b.system_hostname, 'de', { numeric: true });
      case 'service':
        return (a.service_type_label ?? a.service_type).localeCompare(
          b.service_type_label ?? b.service_type,
          'de'
        );
      case 'age': {
        // Infinity - Infinity wäre NaN -> explizit vergleichen.
        const da = driftRowAgeDays(a, now);
        const db = driftRowAgeDays(b, now);
        if (da === db) return 0;
        return da < db ? -1 : 1;
      }
    }
  };
  const sign = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => sign * cmp(a, b));
}

/** Backend-Limit pro Re-Scan-Lauf mit expliziten system_ids. */
export const RESCAN_MAX_SYSTEMS = 200;

export interface FilteredRescanPlan {
  /** Input für useRunHealthChecks - garantiert 422-frei (nie service_type). */
  input: RunHealthChecksInput;
  /** Was der Re-Scan tatsächlich trifft. */
  scope: 'all' | 'customer' | 'systems';
  /** Anzahl eindeutiger Ziel-Systeme; nur bei scope='systems' bekannt. */
  systemCount: number | null;
  /** true wenn system_ids auf RESCAN_MAX_SYSTEMS gekappt wurden. */
  truncated: boolean;
  /** true wenn es keine Ziele gibt - Aktion muss deaktiviert bleiben. */
  empty: boolean;
}

/**
 * Leitet aus Filter + sichtbaren Zeilen ab, wie "Re-Scan alle gefilterten"
 * angefragt werden muss. Das Backend (HealthCheckRunRequest, extra="forbid")
 * kennt als Kriterium NUR customer_id ODER system_ids - KEIN service_type
 * (sonst 422). Sobald irgendein Filter aktiv ist, der sich dort nicht abbilden
 * lässt (Service-Typ, Severity, Drift-Kategorie, EOL, Freitext, Alter, auch
 * only_drifted), schicken wir deshalb die konkreten system_ids der sichtbaren
 * Zeilen - damit trifft der Re-Scan exakt die gefilterte Treffermenge.
 */
export function planFilteredRescan(
  filter: DriftFilter,
  visibleRows: DriftRow[]
): FilteredRescanPlan {
  const narrowing = Boolean(
    filter.serviceType ||
      filter.severity ||
      filter.driftKind ||
      filter.eolOnly ||
      filter.driftOnly ||
      filter.searchText?.trim() ||
      filter.minAgeDays !== undefined
  );
  if (!narrowing) {
    if (filter.customerId) {
      return {
        input: { customerId: filter.customerId },
        scope: 'customer',
        systemCount: null,
        truncated: false,
        empty: false,
      };
    }
    return { input: {}, scope: 'all', systemCount: null, truncated: false, empty: false };
  }
  const unique = Array.from(new Set(visibleRows.map((r) => r.system_id)));
  const systemIds = unique.slice(0, RESCAN_MAX_SYSTEMS);
  return {
    input: { systemIds },
    scope: 'systems',
    systemCount: unique.length,
    truncated: unique.length > RESCAN_MAX_SYSTEMS,
    empty: systemIds.length === 0,
  };
}

// --- Query-Keys ------------------------------------------------------------

/** Nur die Filter, die das Backend serverseitig auswertet. */
interface BackendDriftParams {
  customerId?: string;
  serviceType?: string;
  driftOnly?: boolean;
}

export const driftKeys = {
  all: ['drift'] as const,
  report: (params: BackendDriftParams) => ['drift', 'report', params] as const,
  job: (id: string) => ['drift', 'job', id] as const,
};

// --- Queries ---------------------------------------------------------------

function backendParams(f: DriftFilter): BackendDriftParams {
  const params: BackendDriftParams = {};
  if (f.customerId) params.customerId = f.customerId;
  if (f.serviceType) params.serviceType = f.serviceType;
  if (f.driftOnly) params.driftOnly = true;
  return params;
}

function toQueryParams(p: BackendDriftParams): QueryParams {
  const params: QueryParams = {};
  // Backend erwartet JSON:API-artige filter[...]-Aliase (drift_report.py).
  if (p.customerId) params['filter[customer_id]'] = p.customerId;
  if (p.serviceType) params['filter[service_type]'] = p.serviceType;
  if (p.driftOnly) params['filter[only_drifted]'] = true;
  return params;
}

export function useDriftReport(filter: DriftFilter = {}): ReturnType<
  typeof useQuery<DriftReportResponse>
> {
  // Nur backend-seitige Filter fließen in Fetch + Query-Key ein. severity /
  // eolOnly werden clientseitig angewendet und lösen daher KEINEN Refetch aus.
  const backend = backendParams(filter);
  return useQuery<DriftReportResponse>({
    queryKey: driftKeys.report(backend),
    queryFn: async () => {
      const wire = await api.get<WireDriftReportResponse>(
        '/drift-report',
        toQueryParams(backend)
      );
      return mapDriftReport(wire);
    },
    staleTime: 15_000,
  });
}

/**
 * Polling-Hook für einen laufenden Re-Scan-Job. Polling stoppt automatisch
 * sobald der Job einen End-Status erreicht (completed/failed).
 */
export function useHealthCheckJob(
  jobId: string | null
): ReturnType<typeof useQuery<HealthCheckJob>> {
  return useQuery<HealthCheckJob>({
    queryKey: driftKeys.job(jobId ?? ''),
    queryFn: async () => {
      const wire = await api.get<WireHealthCheckJob>(
        `/health-checks/jobs/${jobId ?? ''}`
      );
      return mapJob(wire);
    },
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3_000;
      if (data.status === 'completed' || data.status === 'failed') return false;
      return 3_000;
    },
    // gcTime: nach Job-Ende wird der Eintrag rasch geräumt - ABER nicht während
    // er aktiv ist, sonst flackert der UI-State.
    gcTime: 60_000,
  });
}

// --- Mutations -------------------------------------------------------------

/**
 * Re-Scan-Body. Das Backend (HealthCheckRunRequest, extra="forbid") akzeptiert
 * ausschließlich customer_id ODER system_ids - KEIN service_type. Ein
 * gesetztes service_type würde 422 auslösen (siehe onRescanFiltered im UI).
 */
interface RunHealthChecksBody {
  system_ids?: string[];
  customer_id?: string;
}

export type RunHealthChecksResponse = WireRunHealthChecksResponse;

export function useRunHealthChecks(): ReturnType<
  typeof useMutation<RunHealthChecksResponse, Error, RunHealthChecksInput>
> {
  const qc = useQueryClient();
  return useMutation<RunHealthChecksResponse, Error, RunHealthChecksInput>({
    mutationFn: (input) => {
      const body: RunHealthChecksBody = {};
      if (input.systemIds && input.systemIds.length > 0) body.system_ids = input.systemIds;
      else if (input.customerId) body.customer_id = input.customerId;
      return api.post<RunHealthChecksResponse, RunHealthChecksBody>(
        '/health-checks/run',
        body
      );
    },
    onSuccess: () => {
      // Drift-Report wird durch Folge-Polling aktualisiert; die alte Liste darf
      // bis dahin sichtbar bleiben (no-op invalidation hier).
      void qc.invalidateQueries({ queryKey: driftKeys.all });
    },
  });
}
